import { ENDPOINTS } from "../../constant/routes";
import { request } from "../../services/requests";

/* ── app health ──────────────────────────────────────────────────── */

/** healthy: 2xx/3xx in time · degraded: slow (≥3s) or 4xx · down: 5xx or no answer */
export type AppStatus = "healthy" | "degraded" | "down";

export interface AppHealthPoint {
  /** ISO, UTC ("Z") */
  checked_at: string;
  /** Answered 2xx/3xx in time; what uptime counts */
  ok: boolean;
  status: AppStatus;
  latency_ms: number | null;
}

export interface AppHealth {
  key: string;
  name: string;
  url: string;
  status: AppStatus;
  ok: boolean;
  status_code: number | null;
  latency_ms: number | null;
  error: string | null;
  checked_at: string | null;
  /** Share (0–1) of checks that were ok, over the last 24h or since history_since; null before any check */
  uptime_24h: number | null;
  /** Mean latency of the ok checks in that window */
  avg_latency_ms: number | null;
  checks_24h: number;
  /** Up to the last 60 checks, oldest first */
  history: AppHealthPoint[];
}

export interface AppsHealthResponse {
  checked_at: string | null;
  /** When the in-memory history began; resets when the admin API restarts */
  history_since: string | null;
  interval_seconds: number;
  summary: { total: number; healthy: number; degraded: number; down: number; issues: number };
  apps: AppHealth[];
}

/** Latest check of every app. `refresh` forces a new round (the server
 * ignores it if the last round is under 10s old). */
export const getAppsHealth = (token: string, opts: { refresh?: boolean; signal?: AbortSignal } = {}) =>
  request(token).get<AppsHealthResponse>(`${ENDPOINTS.HEALTH}/apps`, {
    params: opts.refresh ? { refresh: true } : undefined,
    signal: opts.signal,
  });

/* ── traffic ─────────────────────────────────────────────────────── */

export interface TrafficDay {
  /** YYYY-MM-DD, a calendar day in the requested time zone */
  date: string;
  views: number;
  /** Distinct sessions with a view that day */
  sessions: number;
}

export interface TrafficSummary {
  views: number;
  sessions: number;
  /** signed_in_visitors + anonymous_sessions */
  visitors: number;
  /** Distinct signed-in users */
  signed_in_visitors: number;
  /** Sessions with no signed-in view in the range */
  anonymous_sessions: number;
  /** Views since midnight today in the requested time zone, whatever the range */
  today_views: number;
  /** Over views with a load time in (0, 60s]; null when there are none */
  avg_page_load_ms: number | null;
  median_page_load_ms: number | null;
  page_load_samples: number;
  /** The equal-length period immediately before start */
  previous: { views: number; sessions: number };
}

export interface TrafficBreakdownRow {
  views: number;
  sessions: number;
}

export interface TrafficRecentView {
  event_id: string | null;
  session_id: string | null;
  user_id: string | null;
  username: string | null;
  email: string | null;
  path: string;
  referrer: string | null;
  device_type: string | null;
  browser_name: string | null;
  os_name: string | null;
  country: string | null;
  page_load_time_ms: number | null;
  /** ISO, UTC ("Z") */
  created_at: string | null;
}

export interface TrafficResponse {
  generated_at: string;
  range: {
    start: string;
    end: string;
    tz: string;
    days: number;
    previous_start: string;
    previous_end: string;
  };
  summary: TrafficSummary;
  daily: TrafficDay[];
  /** Top 15 */
  top_pages: (TrafficBreakdownRow & { path: string })[];
  /** Top 10 by host; "Direct" = no referrer, "Other" = not a URL */
  top_referrers: (TrafficBreakdownRow & { referrer: string })[];
  /** Top 10; "Unknown" when missing */
  countries: (TrafficBreakdownRow & { country: string })[];
  /** Lower-cased device type ("mobile", "desktop", …) or "Unknown" */
  devices: (TrafficBreakdownRow & { device: string })[];
  /** Top 8 */
  browsers: (TrafficBreakdownRow & { browser: string })[];
  /** Latest 50 views in range, newest first; page further with getTrafficViews */
  recent: TrafficRecentView[];
}

export interface TrafficParams {
  /** ISO datetimes; [start, end). Default: the last 30 days in tz up to now */
  start?: string;
  end?: string;
  /** IANA time zone used for daily buckets and "today"; default Asia/Singapore */
  tz?: string;
}

export const getTraffic = (token: string, params: TrafficParams = {}, signal?: AbortSignal) =>
  request(token).get<TrafficResponse>(`${ENDPOINTS.ANALYTICS}/traffic`, { params, signal });

export interface TrafficViewsParams {
  /** ISO datetimes; [start, end) */
  start: string;
  end: string;
  limit?: number;
  /** Cursor from the previous page's next_before / next_before_id */
  before?: string;
  before_id?: string;
  /** Only views newer than this row (the newest one shown), for live updates */
  after?: string;
  after_id?: string;
  /** Drop anonymous views on the server so every page is full */
  signed_in_only?: boolean;
}

export interface TrafficViewsPage {
  rows: TrafficRecentView[];
  /** null on the last page; with `after`, set when more new views arrived than `limit` */
  next_before: string | null;
  next_before_id: string | null;
}

export const getTrafficViews = (token: string, params: TrafficViewsParams, signal?: AbortSignal) =>
  request(token).get<TrafficViewsPage>(`${ENDPOINTS.ANALYTICS}/traffic/views`, { params, signal });

export interface TrafficSessionView {
  event_id: string | null;
  path: string;
  referrer: string | null;
  signed_in: boolean;
  page_load_time_ms: number | null;
  /** ISO, UTC ("Z") */
  created_at: string | null;
}

export interface TrafficViewDetail {
  view: TrafficRecentView & {
    visitor_id: string | null;
    is_internal: boolean | null;
    user_agent: string | null;
    os_version: string | null;
    browser_version: string | null;
    language: string | null;
    screen_width: number | null;
    screen_height: number | null;
    ip_address: string | null;
    region: string | null;
    ttfb_ms: number | null;
  };
  user: { user_id: string; username: string | null; email: string | null } | null;
  /** The rest of the visit (same session, within a day either side), oldest first */
  session: { session_id: string | null; views: TrafficSessionView[]; truncated: boolean };
  /** Same browser over all time; null for views from before visitor ids */
  visitor: { visitor_id: string; views: number; sessions: number; first_seen: string | null; last_seen: string | null } | null;
}

export const getTrafficView = (token: string, eventId: string, signal?: AbortSignal) =>
  request(token).get<TrafficViewDetail>(`${ENDPOINTS.ANALYTICS}/traffic/views/${encodeURIComponent(eventId)}`, { signal });
